// Single sign-on provider for the STAFF softphone (OIDC, authorization code +
// PKCE S256). Initialized lazily from the /staff surface only, so the public
// guest caller never contacts the identity provider.
//
// The app is hash-routed, so the provider runs in `responseMode: 'query'`: the
// authorization response comes back in the query string and does not collide
// with the router's fragment. `initSso()` is idempotent (safe to call on every
// mount of the login screen). Naming is generic; the underlying library is an
// implementation detail and never surfaces in UI, URLs or storage keys.

import Keycloak from 'keycloak-js'
import { resolveBrandConfig } from './brandMap'

let provider: Keycloak | null = null
let initPromise: Promise<boolean> | null = null
let tokenListener: ((token: string) => void) | null = null

function appBase(): string {
  return import.meta.env.BASE_URL || '/'
}

function staffLoginUri(): string {
  return `${window.location.origin}${appBase()}#/staff`
}

export function onToken(listener: (token: string) => void): void {
  tokenListener = listener
}

export function initSso(): Promise<boolean> {
  if (initPromise) return initPromise
  const cfg = resolveBrandConfig()
  provider = new Keycloak({ url: cfg.authority, realm: cfg.realm, clientId: cfg.clientId })

  initPromise = provider
    .init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      responseMode: 'query',
      redirectUri: staffLoginUri(),
      silentCheckSsoRedirectUri: `${window.location.origin}${appBase()}silent-check-sso.html`,
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      if (authenticated) {
        provider!.onTokenExpired = () => {
          provider
            ?.updateToken(30)
            .then((refreshed) => {
              if (refreshed && provider?.token && tokenListener) tokenListener(provider.token)
            })
            .catch(() => signIn())
        }
      }
      return Boolean(authenticated)
    })

  return initPromise
}

export function getAccessToken(): string | undefined {
  return provider?.token
}

// Send an unauthenticated staff member to sign in, honoring the brand entry mode.
export function signIn(): void {
  const cfg = resolveBrandConfig()
  if (cfg.entry === 'portal' && cfg.portalUrl) {
    const appId = cfg.portalAppId ? `?app=${encodeURIComponent(cfg.portalAppId)}` : ''
    window.location.href = `${cfg.portalUrl}${appId}`
    return
  }
  provider?.login({ redirectUri: staffLoginUri() })
}

export async function signOut(): Promise<void> {
  if (!provider) return
  await provider.logout({ redirectUri: staffLoginUri() })
}
